import { queryOptions, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query';
import type { ObjectsListResponse, operations } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { objectKeys, objectLookupKey, parseObject, type ObjectListParams, type ObjectRecord } from './entity';

export type ObjectList = Omit<ObjectsListResponse, 'objects'> & { objects: ObjectRecord[] };

export async function getObjects(workspaceId: number, params: ObjectListParams, qc: QueryClient): Promise<ObjectList> {
  const { objectTypeId, ...query } = params;
  const response = await hydraUiApi.objects.getObjectTypesObjects({
    params: { path: { environment_id: String(workspaceId), type_id: objectTypeId }, query },
  });
  const objects = response.objects.map(parseObject);
  for (const record of objects) qc.setQueryData(objectKeys.detail(workspaceId, record.id), record);
  return { ...response, objects };
}

export const getObjectsQueryOptions = (workspaceId: number, params: ObjectListParams, qc: QueryClient) =>
  queryOptions({
    queryKey: objectKeys.list(workspaceId, params),
    queryFn: () => getObjects(workspaceId, params, qc),
    staleTime: 0,
  });

type UseObjectsOptions = {
  params: ObjectListParams;
  queryConfig?: QueryConfig<typeof getObjectsQueryOptions>;
};

export function useObjects({ params, queryConfig }: UseObjectsOptions) {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({ ...getObjectsQueryOptions(workspaceId, params, qc), ...queryConfig });
}

export async function getObject(workspaceId: number, objectTypeId: string, identifier: string): Promise<ObjectRecord> {
  const response = await hydraUiApi.objects.getObjectTypesObjectsByIdentifier({
    params: { path: { environment_id: String(workspaceId), type_id: objectTypeId, identifier } },
  });
  return parseObject(response.object);
}

export const getObjectQueryOptions = (workspaceId: number, objectTypeId: string, identifier: string, qc: QueryClient) =>
  queryOptions({
    queryKey: objectLookupKey(workspaceId, objectTypeId, identifier),
    queryFn: async () => {
      const record = await getObject(workspaceId, objectTypeId, identifier);
      qc.setQueryData(objectKeys.detail(workspaceId, record.id), record);
      return record;
    },
    staleTime: 0,
  });

type UseObjectOptions = {
  objectTypeId: string;
  identifier: string;
  queryConfig?: QueryConfig<typeof getObjectQueryOptions>;
};

export function useObject({ objectTypeId, identifier, queryConfig }: UseObjectOptions) {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({ ...getObjectQueryOptions(workspaceId, objectTypeId, identifier, qc), ...queryConfig });
}

/* ------------------------------------------------------------------------- */
/* Related customers (the object detail page's relationships preview)         */
/* ------------------------------------------------------------------------- */

export type RelatedCustomersParams = { page?: number; size?: number };

/** Prefix covering every page/size variant — the invalidation target after relationship writes. */
export function relatedCustomersAllKey(workspaceId: number, cioObjectId: string) {
  return [...objectKeys.all(workspaceId), 'related-customers', cioObjectId] as const;
}

export function relatedCustomersKey(workspaceId: number, cioObjectId: string, params: RelatedCustomersParams = {}) {
  return [...relatedCustomersAllKey(workspaceId, cioObjectId), params] as const;
}

/** Paginated people related to an object, with the relationship rows sideloaded. */
export async function getRelatedCustomers(
  workspaceId: number,
  cioObjectId: string,
  params: RelatedCustomersParams = {},
) {
  return hydraUiApi.customers.getRelationshipsCustomersRelatedTo({
    params: { path: { environment_id: String(workspaceId), cio_object_id: cioObjectId }, query: params },
  });
}

export const getRelatedCustomersQueryOptions = (
  workspaceId: number,
  cioObjectId: string,
  params: RelatedCustomersParams = {},
) =>
  queryOptions({
    queryKey: relatedCustomersKey(workspaceId, cioObjectId, params),
    queryFn: () => getRelatedCustomers(workspaceId, cioObjectId, params),
    staleTime: 0,
  });

type UseRelatedCustomersOptions = {
  cioObjectId: string;
  params?: RelatedCustomersParams;
  queryConfig?: QueryConfig<typeof getRelatedCustomersQueryOptions>;
};

export function useRelatedCustomers({ cioObjectId, params, queryConfig }: UseRelatedCustomersOptions) {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getRelatedCustomersQueryOptions(workspaceId, cioObjectId, params), ...queryConfig });
}

type LogQuery = NonNullable<operations['get-v1-environments-environment_id-logs']['parameters']['query']>;
export type ObjectLogsParams = Pick<LogQuery, 'type' | 'name' | 'limit' | 'filters'>;

export function queryObjectLogs(workspaceId: number, cioObjectId: string, params: ObjectLogsParams = {}) {
  return hydraUiApi.logs.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: { ...params, cio_object_id: cioObjectId, match_limit: params.filters == null ? undefined : params.limit },
    },
  });
}

export function objectLogsKey(workspaceId: number, cioObjectId: string, params: ObjectLogsParams = {}) {
  return [...objectKeys.all(workspaceId), 'logs', cioObjectId, params] as const;
}

export const getObjectLogsQueryOptions = (workspaceId: number, cioObjectId: string, params: ObjectLogsParams = {}) =>
  queryOptions({
    queryKey: objectLogsKey(workspaceId, cioObjectId, params),
    queryFn: () => queryObjectLogs(workspaceId, cioObjectId, params),
    staleTime: 0,
  });
